/** Parse JSON field from Directus (object or JSON string). */
export function parseJsonField(val, fallback) {
  if (val == null) return fallback;
  if (Array.isArray(val)) return val;
  if (typeof val === "object") return val;
  if (typeof val === "string") {
    const t = val.trim();
    if (!t) return fallback;
    try {
      return JSON.parse(t);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

function normalizeCell(cell) {
  if (cell == null) return null;
  if (typeof cell === "number" || typeof cell === "string") {
    const n = String(cell).trim();
    return n ? { number: n } : null;
  }
  if (typeof cell === "object" && cell.number != null) {
    return { number: String(cell.number) };
  }
  return null;
}

function parseSeatMapRows(seatMap) {
  const map = parseJsonField(seatMap, null);
  if (!map?.rows?.length) return null;
  return map.rows.map((row) =>
    Array.isArray(row) ? row.map(normalizeCell) : []
  );
}

/** Get seat structure + seat_map from a bus item (any API shape). */
export function getBusStructure(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    return {};
  }

  const structure = item.structure ?? {};
  const seatStructure =
    item.seat_structure && typeof item.seat_structure === "object"
      ? item.seat_structure
      : {};

  const seat_map =
    parseJsonField(item.seat_map, null) ||
    parseJsonField(structure.seat_map, null) ||
    parseJsonField(seatStructure.seat_map, null);

  return {
    ...seatStructure,
    ...structure,
    left_seat: structure.left_seat ?? seatStructure.left_seat ?? 2,
    right_seat: structure.right_seat ?? seatStructure.right_seat ?? 2,
    total_rows: structure.total_rows ?? seatStructure.total_rows ?? 0,
    mid_space:
      structure.mid_space ?? seatStructure.mid_space ?? item.mid_space ?? 1,
    removed_seats:
      parseJsonField(structure.removed_seats, null) ??
      parseJsonField(seatStructure.removed_seats, []) ??
      [],
    seat_map,
  };
}

function toSet(values) {
  return new Set((values || []).map((v) => String(v)));
}

function applySeatFlags(rows, options) {
  const {
    reservedSeatsByMale = [],
    reservedSeatsByFemale = [],
    removedSeats = [],
    counterSeats = [],
  } = options;

  const maleSet = toSet(reservedSeatsByMale);
  const femaleSet = toSet(reservedSeatsByFemale);
  const removedSet = toSet(removedSeats);
  const counterSet = toSet(counterSeats);

  let positionId = 1;

  return rows.map((row) =>
    row.map((cell) => {
      if (cell === null) return null;

      const id = positionId;
      positionId += 1;

      if (removedSet.has(String(id))) {
        return null;
      }

      const number = String(cell.number);
      if (removedSet.has(number)) {
        return null;
      }

      const seat = { id, number, neighbors: [] };

      if (maleSet.has(number)) seat.isReservedMale = true;
      if (femaleSet.has(number)) seat.isReservedFemale = true;
      if (counterSet.has(number)) {
        seat.isCounterSeat = true;
      }

      return seat;
    })
  );
}

function assignNeighbors(rows) {
  rows.forEach((row) => {
    let prev = null;
    row.forEach((seat) => {
      if (!seat) {
        prev = null;
        return;
      }
      if (prev) {
        seat.neighbors.push(prev.number);
        prev.neighbors.push(seat.number);
      }
      prev = seat;
    });
  });
  return rows;
}

/** Legacy grid: left → aisle → right, sequential numbering. */
function generateLegacyGrid(
  leftSeat,
  rightSeat,
  totalRows,
  removedSeats,
  midSpace
) {
  const rows = [];
  let seatNumber = 1;
  const removedSet = toSet(removedSeats);

  for (let i = 1; i <= totalRows; i++) {
    const row = [];
    const isLast = i === totalRows;

    for (let j = 0; j < leftSeat; j++) {
      if (removedSet.has(String(seatNumber))) {
        seatNumber += 1;
        row.push(null);
      } else {
        row.push({ number: String(seatNumber++) });
      }
    }

    for (let k = 0; k < midSpace; k++) {
      if (isLast) {
        if (removedSet.has(String(seatNumber))) {
          seatNumber += 1;
          row.push(null);
        } else {
          row.push({ number: String(seatNumber++) });
        }
      } else {
        row.push(null);
      }
    }

    for (let j = 0; j < rightSeat; j++) {
      if (removedSet.has(String(seatNumber))) {
        seatNumber += 1;
        row.push(null);
      } else {
        row.push({ number: String(seatNumber++) });
      }
    }

    rows.push(row);
  }

  return rows;
}

function generateFromSeatMap(structure, options) {
  const mapRows = parseSeatMapRows(structure.seat_map);
  if (!mapRows) return null;
  return assignNeighbors(
    applySeatFlags(mapRows, {
      ...options,
      removedSeats: options.removedSeats ?? structure.removed_seats ?? [],
    })
  );
}

function generateFromConfig(config) {
  const base = getBusStructure(config);
  const merged = {
    ...base,
    ...(config.structure || {}),
    seat_map: parseJsonField(config.structure?.seat_map, null) || base.seat_map,
    mid_space: config.structure?.mid_space ?? base.mid_space ?? 1,
    removed_seats: config.removedSeats ?? base.removed_seats ?? [],
  };

  const flags = {
    reservedSeatsByMale: config.reservedSeatsByMale ?? [],
    reservedSeatsByFemale: config.reservedSeatsByFemale ?? [],
    removedSeats: merged.removed_seats ?? [],
    counterSeats: config.counterSeats ?? [],
  };

  const fromMap = generateFromSeatMap(merged, flags);
  if (fromMap) return fromMap;

  const legacyRows = generateLegacyGrid(
    merged.left_seat ?? 2,
    merged.right_seat ?? 2,
    merged.total_rows ?? 0,
    flags.removedSeats,
    merged.mid_space ?? 1
  );

  return assignNeighbors(applySeatFlags(legacyRows, flags));
}

/**
 * Build seat picker rows.
 * Config: { structure, reservedSeatsByMale, reservedSeatsByFemale, removedSeats, counterSeats }
 * Legacy: generateView(leftSeat, rightSeat, totalRows, reservedMale, reservedFemale, removed, counter, midSpace)
 */
export default function generateView(a, b, c, d, e, f, g, h) {
  if (typeof a === "object" && a !== null && !Array.isArray(a)) {
    if (a.structure != null || a.seat_map != null) {
      return generateFromConfig(a);
    }
    return generateFromConfig({ structure: a, ...a });
  }

  const legacyRows = generateLegacyGrid(a, b, c, f ?? [], h ?? 1);
  return assignNeighbors(
    applySeatFlags(legacyRows, {
      reservedSeatsByMale: d ?? [],
      reservedSeatsByFemale: e ?? [],
      removedSeats: f ?? [],
      counterSeats: g ?? [],
    })
  );
}

export function buildAdjacencyMap(rows) {
  const map = {};
  rows.forEach((row) => {
    row.forEach((seat) => {
      if (seat?.number) map[seat.number] = [...(seat.neighbors || [])];
    });
  });
  return map;
}

/** @deprecated alias for tests */
export const generateViewFromConfig = generateFromConfig;
